import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { laravelOwnsData } from '@/lib/dataOwner';
import { type EsitoNickname } from '@/lib/nicknameEsito';
import { useQlashUser } from '@/hooks/useQlashUser';
import { useSalvaNickname } from '@/hooks/useSalvaNickname';
import { useHaptics } from '@/hooks/useHaptics';

/**
 * **Dove si cambia il nickname** — la schermata che fino al 2026-09-15 non esisteva.
 *
 * ═══ IL GUASTO CHE CHIUDE, misurato ═══
 * La mutazione che scrive il nome aveva **un solo consumatore in tutta l'app**:
 * `components/onboarding/WelcomeStep.tsx`, cioe' uno step del wizard governato da
 * `CloudPref.OnboardingSeen` — che **parte una volta sola per utente**. ⇒ chi aveva saltato
 * quel passo, o l'aveva attraversato prima che il campo esistesse, **non aveva nessuna via
 * nell'app per darsi un nome.**
 *
 * ⚠️ E la via breve **non c'era**: `useCloudPref` espone `{value, ready, set}` e **nessun
 * `clear`**; nessuna riga del sorgente cancella `qlash.onboarding_seen`; il logout
 * (`lib/logto.ts`) ripulisce i token e le chiavi `logto:*` e **non** quella preferenza; e il
 * solo uso di `cloudStorage` oltre a `isSupported` e' `setItem` — nessuna cancellazione,
 * per nessuna preferenza. Anche azzerando il dispositivo, la CloudStorage di Telegram
 * **riporta `'1'`**: e' esattamente cio' per cui quell'hook esiste.
 * ⇒ *Il wizard non e' riapribile. La schermata non e' la via comoda: e' l'unica.*
 *
 * ═══ ⛔ COSA QUESTA SCHERMATA NON RISOLVE ═══
 * Il **«Player» delle classifiche** non viene da qui: lo mette Limulo quando nickname e
 * membership non risolvono ([ADR-0125] `proposed`). Questa schermata fa **esistere** il
 * nickname; che poi la classifica lo mostri e' un altro anello, e va verificato li'.
 *
 * Dove si scrive lo decide `hooks/useSalvaNickname.ts` — un posto solo, perche' la sede sta
 * cambiando (Laravel oggi in produzione, Limulo in ADR-0125).
 */
export function NicknameSettings() {
  const { t } = useTranslation('onboarding');
  const me = useQlashUser();
  const { salva, inCorso, pronto } = useSalvaNickname();
  const haptics = useHaptics();

  const attuale = me?.username ?? '';
  const [bozza, setBozza] = useState<string | null>(null);
  const [esito, setEsito] = useState<EsitoNickname | null>(null);
  const [salvato, setSalvato] = useState(false);

  // ⚠️ `bozza === null` = «non ha ancora toccato il campo» ⇒ si mostra il valore corrente.
  // Una stringa vuota e' un valore TOCCATO e va lasciata vuota: collassare i due casi con un
  // `bozza || attuale` rimetterebbe il nome vecchio sotto le dita di chi lo sta cancellando.
  const mostrato = bozza ?? attuale;
  const pulito = mostrato.trim();
  const invariato = pulito === attuale.trim();

  const onSalva = async () => {
    setEsito(null);
    setSalvato(false);
    const err = await salva(pulito);
    if (err) {
      setEsito(err);
      haptics.notification('error');
      return;
    }
    // 🔑 Il segnale positivo sta QUI e non solo negli haptics: su un telefono con il
    // ritorno aptico spento (e' una preferenza dell'app, `settings/preferences`) gli
    // haptics da soli renderebbero un successo **indistinguibile dal non aver premuto**.
    setSalvato(true);
    setBozza(null);
    haptics.notification('success');
  };

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche la frase sotto. */}
      <header className="sr-only">
        <h1>
          {t('nickname.title', { defaultValue: 'Your nickname' })}
        </h1>
        <p>
          {t('nickname.body', {
            defaultValue: 'This is the name other players see on leaderboards and in matches.',
          })}
        </p>
      </header>

      <label className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-fg">
          {t('welcome.nicknameLabel', { defaultValue: 'Nickname' })}
        </span>
        <input
          type="text"
          value={mostrato}
          maxLength={32}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => {
            setBozza(e.target.value);
            setEsito(null);
            setSalvato(false);
          }}
          className="rounded-xl border border-border bg-bg-secondary p-3 text-sm text-fg outline-none focus:border-brand"
        />
        {/* La regola si mostra solo dove vale davvero: su una build Limulo il server
            normalizza trim/lowercase e rifiuta con 400 fuori da `[a-z0-9_]` 3-32
            (ADR-0125). Su Laravel quella regola NON c'e', e stamparla li' sarebbe un
            divieto inventato — chi la legge si limiterebbe per niente. */}
        {!laravelOwnsData() && (
          <span className="text-xs text-fg-muted">
            {t('nickname.rule', {
              defaultValue: '3–32 characters: lowercase letters, numbers and underscore.',
            })}
          </span>
        )}
      </label>

      {esito && (
        <p className="text-xs text-danger" role="alert">
          {esito === 'gia-preso'
            ? t('welcome.nicknameTaken', {
                defaultValue: 'Questo nickname è già di qualcun altro. Scegline un altro.',
              })
            : esito === 'formato'
              ? t('nickname.badFormat', {
                  defaultValue: 'Questo nickname non è nel formato richiesto.',
                })
              : t('nickname.unknownError', {
                  defaultValue: 'Non siamo riusciti a salvare. Riprova fra poco.',
                })}
        </p>
      )}

      {salvato && (
        <p className="text-xs text-success" role="status">
          {t('nickname.saved', { defaultValue: 'Nickname saved.' })}
        </p>
      )}

      <button
        type="button"
        onClick={() => void onSalva()}
        disabled={inCorso || !pronto || !pulito || invariato}
        className="card-edge rounded-xl bg-brand p-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-40"
      >
        {t('nickname.save', { defaultValue: 'Save' })}
      </button>
    </div>
  );
}
